import {
  getIsSoPortalGroupAdminFromToken,
  resolveAuthToken,
} from "./subscriptionPlan";

export const ROLE_GROUP_ADMIN = "Group Admin";
export const ROLE_SYSTEM_ADMIN = "System Admin";
export const ROLE_COMPANY_ADMIN = "Company Admin";

export const CREATE_MODE = {
  FULL: "full",
  GROUP_ADMIN_BRANCH: "group-admin-branch",
  BRANCH_ONLY: "branch-only",
};

export const isGroupAdminRole = (roleName) => roleName === ROLE_GROUP_ADMIN;

export const isSystemAdminRole = (roleName) => roleName === ROLE_SYSTEM_ADMIN;

export const isCompanyAdminRole = (roleName) => roleName === ROLE_COMPANY_ADMIN;

/** Document extraction / validation service settings are visible and editable only by System Admin. */
export const canManageDocumentServices = (roleName) => isSystemAdminRole(roleName);

export const hasCompanySubscriptionPlan = (subscriptionPlanDetails) =>
  Boolean(subscriptionPlanDetails);

export const hasValidSelectedGroup = () => {
  const groupId = localStorage.getItem("groupId");
  return (
    groupId &&
    groupId !== "" &&
    groupId !== "Select Group" &&
    groupId !== "-1"
  );
};

export const isAllowCompanyCreationEnabled = () =>
  localStorage.getItem("allowCompanyCreation") === "true";

export const isGroupAdminGeneralContactLocked = (roleName) =>
  isGroupAdminRole(roleName);

/** Group Admin may create a company only when a group is selected and allowCompanyCreation is enabled. */
export const canGroupAdminCreateCompany = () => {
  if (!hasValidSelectedGroup()) {
    return false;
  }
  return isAllowCompanyCreationEnabled();
};

/** Group Admin may create a branch when company creation is disabled for the group. */
export const canGroupAdminCreateBranch = () => {
  if (!hasValidSelectedGroup()) {
    return false;
  }
  return !isAllowCompanyCreationEnabled();
};

/** Whether the Companies page + button should be shown for the current role and group. */
export const canShowCompanyCreateButton = (roleName) => {
  if (!hasValidSelectedGroup()) {
    return false;
  }
  if (isSystemAdminRole(roleName)) {
    return true;
  }
  if (isGroupAdminRole(roleName)) {
    return canGroupAdminCreateCompany() || canGroupAdminCreateBranch();
  }
  if (isCompanyAdminRole(roleName)) {
    return false;
  }
  return true;
};

/** Drawer mode: full company create (with branch toggle) vs branch-only create. */
export const getCompanyCreateMode = (roleName, subscriptionPlanDetails = null) => {
  if (isSystemAdminRole(roleName)) {
    return CREATE_MODE.FULL;
  }
  if (isGroupAdminRole(roleName)) {
    return isAllowCompanyCreationEnabled()
      ? CREATE_MODE.FULL
      : CREATE_MODE.GROUP_ADMIN_BRANCH;
  }
  if (isCompanyAdminRole(roleName)) {
    return CREATE_MODE.BRANCH_ONLY;
  }
  if (!isAllowCompanyCreationEnabled()) {
    return CREATE_MODE.BRANCH_ONLY;
  }
  return CREATE_MODE.FULL;
};

export const persistAllowCompanyCreationForGroup = (group) => {
  if (!group || !group._id) {
    localStorage.removeItem("allowCompanyCreation");
    return;
  }
  localStorage.setItem("allowCompanyCreation", String(!!group.allowCompanyCreation));
};

export const mapGroupToCompanyContactPayload = (group) => {
  if (!group) return {};
  const address = group.address || {};
  const state = address.state || {};
  return {
    email: group.email || "",
    contactNumber: group.contactNumber || "",
    addressLine1: address.addressLine1 || "",
    addressLine2: address.addressLine2 || "",
    addressLine3: address.addressLine3 || "",
    postalZone: address.postalZone || "",
    city: address.city || "",
    stateName: state.name || "",
    stateCode: state.code || "",
    countryCode: group.country?.code || "",
    countryName: group.country?.name || "",
    groupId: group._id || localStorage.getItem("groupId") || "",
  };
};

const BRANCH_CREATE_EDITABLE_FIELDS = new Set(["parentCompanyId", "branchName"]);

const BRANCH_CONTACT_FIELDS = new Set(["email", "contactNumber"]);

const BRANCH_ADDRESS_FIELDS = new Set([
  "addressLine1",
  "addressLine2",
  "addressLine3",
  "postalZone",
  "city",
  "state",
]);

const isBranchAddressField = (fieldKey) => BRANCH_ADDRESS_FIELDS.has(fieldKey);

const isBranchContactSectionField = (fieldKey) =>
  BRANCH_CONTACT_FIELDS.has(fieldKey) || isBranchAddressField(fieldKey);

const isBranchEditableField = (fieldKey) =>
  BRANCH_CREATE_EDITABLE_FIELDS.has(fieldKey) || isBranchContactSectionField(fieldKey);

export const isGroupOrCompanyAdmin = (roleName) =>
  isGroupAdminRole(roleName) || isCompanyAdminRole(roleName);

/** Group Admin cannot edit general or contact info when the company has a subscription plan. Company Admin is always read-only on those sections when editing. */
export const isPortalGeneralContactLockedOnEdit = ({
  roleName,
  subscriptionPlanDetails,
  isCreateFlow = false,
}) => {
  if (isCreateFlow) {
    return false;
  }
  if (isCompanyAdminRole(roleName)) {
    return true;
  }
  return (
    isGroupAdminRole(roleName) && hasCompanySubscriptionPlan(subscriptionPlanDetails)
  );
};

/** Group Admin may edit parent company general and contact info when there is no subscription plan. */
export const isPortalParentCompanyEditableOnEdit = ({
  roleName,
  subscriptionPlanDetails,
  isCreateFlow = false,
  isBranch = false,
}) =>
  !isCreateFlow &&
  isGroupAdminRole(roleName) &&
  !isBranch &&
  !hasCompanySubscriptionPlan(subscriptionPlanDetails);

export const getPortalGeneralContactLockedMessage = (
  roleName,
  section = "general",
  token
) => {
  if (isCompanyAdminRole(roleName)) {
    return "General and contact information is read-only. You can update Configuration and Notification only.";
  }
  if (
    isGroupAdminRole(roleName) &&
    !getIsSoPortalGroupAdminFromToken(resolveAuthToken(token))
  ) {
    return "General and contact information is read-only for your account.";
  }
  return section === "contact"
    ? "To edit contact information, use the Self Onboarding Portal."
    : "To edit general information, use the Self Onboarding Portal.";
};

/** Group Admin creating a new company (not branch) when the group allows company creation. */
export const isGroupAdminFullCompanyCreate = ({
  roleName,
  isCreateFlow = false,
  isBranch = false,
}) =>
  isGroupAdminRole(roleName) &&
  isCreateFlow &&
  !isBranch &&
  isAllowCompanyCreationEnabled();

/** Whether a General Info / Contact field stays editable for Group Admin / Company Admin flows. */
export const isGeneralContactFieldEditable = ({
  roleName,
  fieldKey,
  isCreateFlow = false,
  isBranch = false,
  subscriptionPlanDetails = null,
}) => {
  if (isSystemAdminRole(roleName)) {
    if (!isCreateFlow && isBranch) {
      return isBranchContactSectionField(fieldKey);
    }
    return true;
  }

  if (!isGroupOrCompanyAdmin(roleName)) {
    return true;
  }

  if (
    isPortalGeneralContactLockedOnEdit({
      roleName,
      subscriptionPlanDetails,
      isCreateFlow,
    })
  ) {
    return false;
  }

  if (
    isPortalParentCompanyEditableOnEdit({
      roleName,
      subscriptionPlanDetails,
      isCreateFlow,
      isBranch,
    })
  ) {
    return true;
  }

  if (isGroupAdminFullCompanyCreate({ roleName, isCreateFlow, isBranch })) {
    return true;
  }

  if (isCreateFlow && isBranch) {
    return isBranchEditableField(fieldKey);
  }

  if (!isCreateFlow && isBranch) {
    return isBranchContactSectionField(fieldKey);
  }

  if (isCreateFlow) {
    return BRANCH_CREATE_EDITABLE_FIELDS.has(fieldKey);
  }

  return false;
};
